import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Grid,
  Alert,
} from '@mui/material';
import type { ProviderPlaceItem } from '../types/providerPlace';
import type { ProviderStorageBoxRow } from '../types/storageBox';
import type { PlaceStorageMode } from '../utils/placeStorageMode';
import { getProviderPlaceDisplayColor } from '../utils/providerPlaceColor';
import { comparePlacesByLabel } from '../utils/comparePlaceLabels';

interface LocationFieldsProps {
  template: any;
  locationData: Record<string, any>;
  onLocationDataChange: (key: string, value: any) => void;
  providerConfig?: Record<string, any>;
  /** Lieux définis pour ce partenaire (remplace chambres JSON / zones JSON) */
  providerItems?: ProviderPlaceItem[];
  /** Boîtes de stockage (prioritaire sur la sélection lieu seul). */
  storageBoxes?: ProviderStorageBoxRow[];
  /** Si vrai : pas de sélecteur de boîte ici (choix différé, ex. modale à l’enregistrement). */
  hideStorageBox?: boolean;
  /** combined = boîtes par lieu ; split = lieu de perte + stockage central ; central = boîtes + lieu texte libre */
  placeStorageMode?: PlaceStorageMode;
}

const LocationFields: React.FC<LocationFieldsProps> = ({
  template,
  locationData,
  onLocationDataChange,
  providerConfig,
  providerItems = [],
  storageBoxes = [],
  hideStorageBox = false,
  placeStorageMode = 'combined',
}) => {
  const isSplitMode = placeStorageMode === 'split';
  const isCentralMode = placeStorageMode === 'central';
  const usesCentralBoxes = isSplitMode || isCentralMode;
  const activeItems = providerItems.filter((i) => i.is_active !== false);
  const activeBoxes = storageBoxes.filter((b) => b.is_active !== false);
  const fields = template?.fields || {};

  const renderFlatStorageBoxSelect = (label: string, required = true) => {
    if (activeBoxes.length === 0) {
      return (
        <Alert severity="warning" sx={{ width: '100%' }}>
          Aucune boîte de stockage. Créez des boîtes dans le menu <strong>Stockage</strong>.
        </Alert>
      );
    }
    const sid = locationData.storage_box_id;
    const value = sid != null && sid !== '' ? String(sid) : '';
    const sorted = [...activeBoxes].sort((a, b) =>
      (a.category?.name ?? '').localeCompare(b.category?.name ?? '', 'fr', { sensitivity: 'base' }) ||
      (a.name ?? '').localeCompare(b.name ?? '', 'fr', { numeric: true, sensitivity: 'base' })
    );
    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              onLocationDataChange('storage_box_id', '');
              return;
            }
            onLocationDataChange('storage_box_id', Number(raw));
          }}
          label={label}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {sorted.map((box) => (
            <MenuItem key={box.id} value={String(box.id)}>
              {box.category?.name ? `${box.category.name} — ${box.name}` : box.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderFoundPlaceSelect = (label: string, required = true) =>
    renderProviderPlaceSelect(label, required, true);

  const renderStorageBoxSelect = (label: string, required = true) => {
    if (usesCentralBoxes) {
      return renderFlatStorageBoxSelect(label, required);
    }
    if (activeBoxes.length === 0) {
      return (
        <Alert severity="warning" sx={{ width: '100%' }}>
          Aucune boîte de stockage. Créez des boîtes par lieu dans le menu <strong>Stockage</strong>.
        </Alert>
      );
    }
    const sid = locationData.storage_box_id;
    const value = sid != null && sid !== '' ? String(sid) : '';
    const grouped = activeBoxes.reduce<
      Record<string, { label: string; color: string; boxes: ProviderStorageBoxRow[] }>
    >((acc, box) => {
      const placeLabel =
        (box.provider_place?.display_name || box.provider_place?.name || 'Lieu').trim() || 'Lieu';
      const key = String(box.provider_place?.id ?? placeLabel);
      if (!acc[key]) {
        acc[key] = {
          label: placeLabel,
          color: getProviderPlaceDisplayColor(box.provider_place),
          boxes: [],
        };
      }
      acc[key].boxes.push(box);
      return acc;
    }, {});
    const placeGroups = Object.values(grouped).sort((a, b) =>
      a.label.localeCompare(b.label, 'fr')
    );
    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              onLocationDataChange('storage_box_id', '');
              onLocationDataChange('provider_item_id', '');
              onLocationDataChange('room', '');
              return;
            }
            const id = Number(raw);
            const box = activeBoxes.find((b) => b.id === id);
            onLocationDataChange('storage_box_id', id);
            if (box?.provider_item_id != null) {
              onLocationDataChange('provider_item_id', box.provider_item_id);
            }
            if (box && template?.type === 'hotel') {
              onLocationDataChange('room', box.name || '');
            }
          }}
          label={label}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {placeGroups.map((group) => [
            <ListSubheader
              key={`sb-place-${group.label}`}
              sx={{ color: group.color, fontWeight: 700 }}
            >
              {group.label}
            </ListSubheader>,
            ...group.boxes.map((box) => (
              <MenuItem key={box.id} value={String(box.id)} sx={{ borderLeft: `3px solid ${group.color}` }}>
                {box.name}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
    );
  };

  const renderProviderPlaceSelect = (label: string, required = true, asFoundPlace = false) => {
    if (activeItems.length === 0) return null;
    const pid = asFoundPlace
      ? locationData.found_provider_item_id ?? locationData.provider_item_id
      : locationData.provider_item_id;
    const value = pid != null && pid !== '' ? String(pid) : '';
    const places = [...activeItems].sort(comparePlacesByLabel);
    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              if (asFoundPlace) {
                onLocationDataChange('found_provider_item_id', '');
                onLocationDataChange('provider_item_id', '');
              } else {
                onLocationDataChange('provider_item_id', '');
              }
              onLocationDataChange('room', '');
              return;
            }
            const id = Number(raw);
            const item = activeItems.find((i) => i.id === id);
            if (asFoundPlace) {
              onLocationDataChange('found_provider_item_id', id);
              onLocationDataChange('provider_item_id', id);
            } else {
              onLocationDataChange('provider_item_id', id);
            }
            if (item && template?.type === 'hotel') {
              onLocationDataChange('room', item.name);
            }
          }}
          label={label}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {places.map((item) => {
            const color = getProviderPlaceDisplayColor(item);
            return (
              <MenuItem
                key={item.id}
                value={String(item.id)}
                sx={{ borderLeft: `3px solid ${color}` }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  {item.display_name || item.name}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const renderRoomSelector = () => {
    if (activeBoxes.length > 0) {
      if (hideStorageBox) {
        return null;
      }
      return renderStorageBoxSelect('Stockage (boîte)', true);
    }
    if (activeItems.length > 0) {
      return renderProviderPlaceSelect('Stockage (par lieu)', true);
    }

    let rooms: { number: string; type: string; floor: string }[] = [];
    if (providerConfig && providerConfig.rooms && Array.isArray(providerConfig.rooms)) {
      rooms = providerConfig.rooms;
    } else {
      rooms = [
        { number: '101', type: 'standard', floor: '1' },
        { number: '102', type: 'standard', floor: '1' },
        { number: '201', type: 'superior', floor: '2' },
        { number: '202', type: 'superior', floor: '2' },
        { number: 'Suite 1', type: 'suite', floor: '3' },
      ];
    }

    const roomOptions = rooms.map((room) => ({
      value: room.number,
      label: `Chambre ${room.number}${room.type ? ` (${room.type})` : ''}`,
    }));

    return (
      <FormControl fullWidth required>
        <InputLabel>Chambre</InputLabel>
        <Select
          value={(locationData || {}).room ?? ''}
          onChange={(e) => onLocationDataChange('room', e.target.value)}
          label="Chambre"
        >
          {roomOptions.map((option, index) => (
            <MenuItem key={`room-${option.value}-${index}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderField = (fieldName: string, fieldConfig: any) => {
    const value = (locationData || {})[fieldName] ?? '';

    if (fieldConfig.type === 'array' && fieldName === 'rooms') {
      return renderRoomSelector();
    }

    if (fieldConfig.type === 'array' && fieldName === 'zones') {
      if (activeBoxes.length > 0) {
        if (hideStorageBox) {
          return null;
        }
        return renderStorageBoxSelect('Stockage (boîte)', false);
      }
      if (activeItems.length > 0) {
        return renderProviderPlaceSelect('Stockage', false);
      }
      return (
        <Alert severity="info" sx={{ width: '100%' }}>
          Définissez les boîtes dans <strong>Stockage</strong>, puis sélectionnez-en une ici.
        </Alert>
      );
    }

    if (fieldConfig.type === 'array' || fieldConfig.type === 'object') {
      return null;
    }

    switch (fieldConfig.type) {
      case 'string':
      case 'text':
        return (
          <TextField
            fullWidth
            label={fieldConfig.label}
            value={value}
            onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
            required={fieldConfig.required}
            placeholder={fieldConfig.placeholder}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth required={fieldConfig.required}>
            <InputLabel>{fieldConfig.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
              label={fieldConfig.label}
            >
              {fieldConfig.options && typeof fieldConfig.options === 'object'
                ? Object.entries(fieldConfig.options).map(([key, label], index) => (
                    <MenuItem key={`${fieldName}-${key}-${index}`} value={key}>
                      {label as string}
                    </MenuItem>
                  ))
                : fieldConfig.options?.map((option: string, index: number) => (
                    <MenuItem key={`${fieldName}-${option}-${index}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={fieldConfig.label}
            value={value}
            onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
            required={fieldConfig.required}
            inputProps={{
              min: fieldConfig.min,
              max: fieldConfig.max,
            }}
          />
        );

      case 'time':
        return (
          <TextField
            fullWidth
            type="time"
            label={fieldConfig.label}
            value={value}
            onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
            required={fieldConfig.required}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            label={fieldConfig.label}
            value={value}
            onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
            required={fieldConfig.required}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'boolean':
        return (
          <FormControl fullWidth>
            <InputLabel>{fieldConfig.label}</InputLabel>
            <Select
              value={value ? 'true' : 'false'}
              onChange={(e) => onLocationDataChange(fieldName, e.target.value === 'true')}
              label={fieldConfig.label}
            >
              <MenuItem value="true">Oui</MenuItem>
              <MenuItem value="false">Non</MenuItem>
            </Select>
          </FormControl>
        );

      default:
        return (
          <TextField
            fullWidth
            label={fieldConfig.label}
            value={value}
            onChange={(e) => onLocationDataChange(fieldName, e.target.value)}
            required={fieldConfig.required}
          />
        );
    }
  };

  const showGenericPlace =
    template?.type !== 'hotel' &&
    (activeBoxes.length > 0 || activeItems.length > 0) &&
    !fields.rooms &&
    !fields.zones;

  const wrapGridField = (key: string, content: React.ReactNode) => {
    if (content == null) {
      return null;
    }
    return (
      <Grid item xs={12} sm={6} key={key}>
        {content}
      </Grid>
    );
  };

  return (
    <>
      {isCentralMode &&
        activeItems.length > 0 &&
        wrapGridField(
          'found-place-central-select',
          renderFoundPlaceSelect('Lieu de trouvaille', false)
        )}
      {isCentralMode &&
        wrapGridField(
          'found-place-central-free',
          <TextField
            fullWidth
            label={activeItems.length > 0 ? 'Précision (optionnel)' : 'Lieu de trouvaille'}
            value={(locationData || {}).room ?? ''}
            onChange={(e) => onLocationDataChange('room', e.target.value)}
            placeholder="Ex. Chambre 312, lobby, restaurant…"
            helperText={
              activeItems.length > 0
                ? 'Complément libre si besoin (en plus du lieu sélectionné).'
                : 'Texte libre (optionnel). Créez des chambres côté admin pour les proposer en liste.'
            }
          />
        )}
      {isCentralMode &&
        activeBoxes.length > 0 &&
        !hideStorageBox &&
        wrapGridField('storage-box-central', renderFlatStorageBoxSelect('Stockage (boîte)', true))}
      {isSplitMode &&
        activeItems.length > 0 &&
        wrapGridField('found-place-split', renderFoundPlaceSelect('Lieu de perte', true))}
      {isSplitMode &&
        activeBoxes.length > 0 &&
        !hideStorageBox &&
        wrapGridField('storage-box-split', renderFlatStorageBoxSelect('Stockage (boîte)', true))}
      {!usesCentralBoxes &&
        !template &&
        activeBoxes.length > 0 &&
        !hideStorageBox &&
        wrapGridField('storage-box-no-template', renderStorageBoxSelect('Stockage (boîte)', true))}
      {!usesCentralBoxes &&
        !template &&
        activeBoxes.length === 0 &&
        activeItems.length > 0 &&
        wrapGridField('place-no-template', renderProviderPlaceSelect('Stockage', true))}
      {!usesCentralBoxes &&
        showGenericPlace &&
        activeBoxes.length > 0 &&
        !hideStorageBox &&
        wrapGridField('storage-box-generic', renderStorageBoxSelect('Stockage (boîte)', true))}
      {!usesCentralBoxes &&
        showGenericPlace &&
        activeBoxes.length === 0 &&
        wrapGridField('place-generic', renderProviderPlaceSelect('Stockage', true))}
      {!isCentralMode &&
        Object.entries(fields).map(([fieldName, fieldConfig]) =>
          wrapGridField(`template-${fieldName}`, renderField(fieldName, fieldConfig as any))
        )}
    </>
  );
};

export default LocationFields;
